import { API_BASES } from "../../config/apiConfig";
import { formatApiError } from "../../util/formatApiError";
import { showToast } from "../../util/appToast";

const EVENTS_API_BASE = API_BASES.EVENTS;

const parseResponse = async (response, opts) => {
  opts = opts || {};
  let data;
  try {
    data = await response.json();
  } catch (_) {
    data = {};
  }
  if (!response.ok || data.success === false) {
    const error = formatApiError(data.message || data || "Request failed");
    if (!opts.silent && response.status !== 401) showToast(error, "error");
    throw new Error(error);
  }
  return data.payload;
};

const jsonFetch = async (path, options = {}) => {
  const silent = !!options.silent;
  const rest = Object.assign({}, options);
  delete rest.silent;
  const response = await fetch(`${EVENTS_API_BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    ...rest
  });
  return parseResponse(response, { silent });
};

const staffJsonFetch = async (path, token, options = {}) => {
  const headers = { "Content-Type": "application/json", ...(options.headers || {}) };
  if (token) headers.Authorization = "Bearer " + token;
  const response = await fetch(`${EVENTS_API_BASE}${path}`, { ...options, headers });
  let data;
  try {
    data = await response.json();
  } catch (_) {
    data = {};
  }
  if (response.status === 401) {
    try {
      const mod = await import("../../Staff/session/staffSession");
      mod.setStaffToken(null);
      mod.setStaffPayload(null);
    } catch (_) {
      /* circular-safe fallback — session hook still clears on next refresh */
    }
    const error = formatApiError(data.message || data || "Session expired, please log in again.");
    showToast(error, "error");
    throw new Error(error);
  }
  if (!response.ok || data.success === false) {
    const error = formatApiError(data.message || data || "Request failed");
    if (!options.silent) showToast(error, "error");
    throw new Error(error);
  }
  return data.payload;
};

export const eventsApi = {
  listEvents: (params) => {
    var q = "";
    if (params && params.search) {
      q = "?search=" + encodeURIComponent(params.search);
    }
    return jsonFetch("/list/" + q);
  },
  /** Public live occupancy (paid + confirmed bookings); no login required. */
  publicOccupancy: eventId =>
    jsonFetch(`/occupancy/?event_id=${encodeURIComponent(eventId)}`),
  /** Public site feedback (rating + comment); no login required. */
  submitSiteFeedback: body =>
    jsonFetch("/feedback/", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  /** Public testimonials (3+ stars, opted-in); no login required. */
  publicTestimonials: () => jsonFetch("/testimonials/"),
  /** Optional ``staffToken`` scopes ``parkings`` + ``occupancy`` like staff APIs. */
  getEventDetail: (eventId, staffToken) => {
    const headers = { "Content-Type": "application/json" };
    if (staffToken) headers.Authorization = "Bearer " + staffToken;
    return jsonFetch(`/detail/?event_id=${encodeURIComponent(eventId)}`, { headers });
  },
  listCommittees: eventId =>
    jsonFetch(`/committees/?event_id=${encodeURIComponent(eventId)}`),
  quote: (body, opts) =>
    jsonFetch("/quote/", {
      method: "POST",
      body: JSON.stringify(body),
      silent: !!(opts && opts.silent)
    }),
  createBooking: body =>
    jsonFetch("/booking/create/", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  sendOtp: body =>
    jsonFetch("/booking/otp/send/", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  verifyOtp: body =>
    jsonFetch("/booking/otp/verify/", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  initPayment: body =>
    jsonFetch("/payment/init/", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  lookupBooking: function (params) {
    if (typeof params === "string") params = { booking_id: params };
    var qs = Object.keys(params).filter(function (k) { return params[k]; }).map(function (k) { return k + "=" + encodeURIComponent(params[k]); }).join("&");
    return jsonFetch("/booking/lookup/?" + qs);
  },
  searchCertificates: function (params) {
    var qs = Object.keys(params).filter(function (k) { return params[k]; }).map(function (k) { return k + "=" + encodeURIComponent(params[k]); }).join("&");
    return jsonFetch("/certificate/search/?" + qs);
  },

  /** Event staff (website prebook; requires Bearer token from staffLogin) */
  staffLogin: body =>
    staffJsonFetch("/staff/login/", null, { method: "POST", body: JSON.stringify(body) }),
  staffLogout: token =>
    staffJsonFetch("/staff/logout/", token, { method: "POST", body: "{}" }),
  staffSession: token => staffJsonFetch("/staff/session/", token, { method: "GET" }),
  staffPrebook: (token, body) =>
    staffJsonFetch("/staff/prebook/", token, { method: "POST", body: JSON.stringify(body) }),
  /* Offline-cash desk: OTP-verify the committee member authorising the sale.
     Verify returns a token that is reused for every guest on that shift. */
  committeeMemberOtpSend: (token, body) =>
    staffJsonFetch("/staff/committee-member/otp/send/", token, {
      method: "POST",
      body: JSON.stringify(body || {}),
    }),
  committeeMemberOtpVerify: (token, body) =>
    staffJsonFetch("/staff/committee-member/otp/verify/", token, {
      method: "POST",
      body: JSON.stringify(body || {}),
    }),
  /* `opts.silent` keeps the boot-time token probe from toasting on failure. */
  committeeMemberTokenCheck: (token, body, opts) =>
    staffJsonFetch("/staff/committee-member/token/check/", token, {
      method: "POST",
      body: JSON.stringify(body || {}),
      silent: !!(opts && opts.silent),
    }),
  /** Real-time parking occupancy (paid + confirmed; negatives = over plan). */
  staffOccupancy: (token, eventId) =>
    staffJsonFetch(
      "/staff/occupancy/?event_id=" + encodeURIComponent(eventId),
      token,
      { method: "GET" }
    ),
  /** Paginated public site feedback (super admin + event admin only). */
  staffSiteFeedbackList: (token, params) => {
    const sp = new URLSearchParams();
    if (params && typeof params === "object") {
      Object.keys(params).forEach(k => {
        const v = params[k];
        if (v != null && v !== "") sp.set(k, String(v));
      });
    }
    const qs = sp.toString();
    return staffJsonFetch("/staff/feedback/" + (qs ? "?" + qs : ""), token, { method: "GET" });
  },
  /** Active + inactive events for ops analytics (Bearer admin) */
  staffEventsList: token =>
    staffJsonFetch("/staff/events/", token, { method: "GET" }),
  staffBookings: function (token, params) {
    var parts = [];
    params = params || {};
    Object.keys(params).forEach(function (k) {
      var v = params[k];
      if (v === undefined || v === null || v === "") return;
      parts.push(encodeURIComponent(k) + "=" + encodeURIComponent(v));
    });
    var qs = parts.length ? "?" + parts.join("&") : "";
    return staffJsonFetch("/staff/bookings/" + qs, token, { method: "GET" });
  },
  staffBookingDetail: function (token, bookingId) {
    return staffJsonFetch("/staff/bookings/" + encodeURIComponent(bookingId) + "/", token, { method: "GET" });
  },
  // VVIP Exemption
  listExemptions: function (params) {
    var qs = "";
    if (params) {
      var parts = [];
      if (params.event_id) parts.push("event_id=" + encodeURIComponent(params.event_id));
      if (params.search) parts.push("search=" + encodeURIComponent(params.search));
      if (params.active !== undefined) parts.push("active=" + params.active);
      if (parts.length) qs = "?" + parts.join("&");
    }
    return jsonFetch("/vvip-exemptions/" + qs);
  },
  createExemption: function (body) {
    return jsonFetch("/vvip-exemptions/create/", { method: "POST", body: JSON.stringify(body) });
  },
  getExemptionDetail: function (id) {
    return jsonFetch("/vvip-exemptions/" + id + "/");
  },
  updateExemption: function (id, body) {
    return jsonFetch("/vvip-exemptions/" + id + "/update/", { method: "POST", body: JSON.stringify(body) });
  },
  getExemptionByCode: function (code) {
    return jsonFetch("/vvip-exemptions/by-code/?code=" + encodeURIComponent(code));
  },
  generateExemptionQr: function (id) {
    return jsonFetch("/vvip-exemptions/" + id + "/generate-qr/", { method: "POST" });
  },
  getExemptionVehicles: function (id, params) {
    var parts = [];
    if (params) {
      if (params.search) parts.push("search=" + encodeURIComponent(params.search));
      if (params.page) parts.push("page=" + params.page);
      if (params.page_size) parts.push("page_size=" + params.page_size);
    }
    var qs = parts.length ? "?" + parts.join("&") : "";
    return jsonFetch("/vvip-exemptions/" + id + "/vehicles/" + qs);
  },
  addExemptionVehicle: function (id, body) {
    return jsonFetch("/vvip-exemptions/" + id + "/vehicles/add/", { method: "POST", body: JSON.stringify(body) });
  },
  deleteExemptionVehicle: function (id) {
    return jsonFetch("/vvip-exemption-vehicles/" + id + "/delete/", { method: "POST" });
  },
  // ── Unified SMS pay-link + receipt endpoints ───────────────────
  // ``kind`` is one of: event_booking, valet_walkin, cart_walkin, vendor_order.
  // These drive /pay/:kind/:ref and /receipt/:kind/:ref — every payable
  // entity in the system funnels through the same code path.
  payLinkSummary: function (kind, ref) {
    return jsonFetch("/pay-link/" + encodeURIComponent(kind) + "/" + encodeURIComponent(ref) + "/summary/");
  },
  payLinkSession: function (kind, ref) {
    return jsonFetch(
      "/pay-link/" + encodeURIComponent(kind) + "/" + encodeURIComponent(ref) + "/session/",
      { method: "POST", body: "{}" },
    );
  },
  payLinkSend: function (token, kind, ref, body) {
    return staffJsonFetch(
      "/pay-link/" + encodeURIComponent(kind) + "/" + encodeURIComponent(ref) + "/send/",
      token,
      { method: "POST", body: JSON.stringify(body || {}) },
    );
  },
  receipt: function (kind, ref) {
    return jsonFetch("/receipt/" + encodeURIComponent(kind) + "/" + encodeURIComponent(ref) + "/");
  },
  receiptSendWhatsApp: function (kind, ref) {
    return jsonFetch(
      "/receipt/" + encodeURIComponent(kind) + "/" + encodeURIComponent(ref) + "/whatsapp/",
      { method: "POST", body: "{}", headers: { "Content-Type": "application/json" } },
    );
  },
  receiptResendSms: function (token, kind, ref) {
    return staffJsonFetch(
      "/receipt/" + encodeURIComponent(kind) + "/" + encodeURIComponent(ref) + "/resend-sms/",
      token, { method: "POST", body: "{}" },
    );
  },
  markCashPaid: function (token, kind, ref, body) {
    return staffJsonFetch(
      "/mark-cash-paid/" + encodeURIComponent(kind) + "/" + encodeURIComponent(ref) + "/",
      token, { method: "POST", body: JSON.stringify(body || {}) },
    );
  },
  staffRazorpayUpiQr: function (token, body) {
    return staffJsonFetch(
      "/staff/payment/razorpay-upi-qr/",
      token,
      { method: "POST", body: JSON.stringify(body || {}) },
    );
  },

  listAllVehicles: function (params) {
    var parts = [];
    if (params) {
      if (params.event_id) parts.push("event_id=" + encodeURIComponent(params.event_id));
      if (params.search) parts.push("search=" + encodeURIComponent(params.search));
      if (params.vehicle_type) parts.push("vehicle_type=" + encodeURIComponent(params.vehicle_type));
      if (params.verified !== undefined && params.verified !== "") parts.push("verified=" + params.verified);
      if (params.page) parts.push("page=" + params.page);
      if (params.page_size) parts.push("page_size=" + params.page_size);
    }
    var qs = parts.length ? "?" + parts.join("&") : "";
    return jsonFetch("/vvip-vehicles/" + qs);
  }
};
